import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'
  
  // Exchange rate: 1 USD = 125 ETB (Ethiopian Birr)
  const USD_TO_ETB = 125;

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/plans`);
      if (response.data.success) {
        // Transform API data to component format
        const transformedPlans = response.data.plans.map(plan => ({
          id: plan.plan_id,
          name: plan.name,
          priceMonthly: plan.price_monthly,
          priceYearly: plan.price_yearly,
          description: plan.description,
          features: plan.features,
          popular: plan.plan_id === 'tier2', // Mark tier2 as popular
          limits: plan.limits,
        }));
        setPlans(transformedPlans);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      // Fallback to default plans if API fails
      setPlans([
        {
          id: 'tier1',
          name: 'Basic',
          priceMonthly: 29,
          priceYearly: 278, // ~20% discount
          description: 'Perfect for small businesses just getting started',
          features: [
            '1 User Account',
            '100 Products',
            '1 Warehouse',
            'Basic Reports',
            'Email Support',
            'Mobile Access',
          ],
          popular: false,
        },
        {
          id: 'tier2',
          name: 'Professional',
          priceMonthly: 79,
          priceYearly: 758, // ~20% discount
          description: 'Ideal for growing businesses',
          features: [
            '5 User Accounts',
            '1,000 Products',
            '3 Warehouses',
            'Advanced Reports',
            'Priority Support',
            'API Access',
            'Custom Branding',
          ],
          popular: true,
        },
        {
          id: 'tier3',
          name: 'Business',
          priceMonthly: 199,
          priceYearly: 1910, // ~20% discount
          description: 'For established businesses with complex needs',
          features: [
            '20 User Accounts',
            '10,000 Products',
            '10 Warehouses',
            'Custom Reports',
            '24/7 Support',
            'Dedicated Database',
            'Advanced Analytics',
            'White Label',
          ],
          popular: false,
        },
        {
          id: 'enterprise',
          name: 'Enterprise',
          priceMonthly: 'Custom',
          priceYearly: 'Custom',
          description: 'Tailored solutions for large organizations',
          features: [
            'Unlimited Users',
            'Unlimited Products',
            'Unlimited Warehouses',
            'Custom Features',
            'Dedicated Support',
            'SLA Guarantee',
            'On-premise Option',
            'Custom Integration',
          ],
          popular: false,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const calculateSavings = (monthly, yearly) => {
    if (typeof monthly !== 'number' || typeof yearly !== 'number') return 0;
    const monthlyCost = monthly * 12;
    const savings = monthlyCost - yearly;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return percentage;
  };

  const getDisplayPrice = (plan) => {
    if (billingCycle === 'monthly') {
      return typeof plan.priceMonthly === 'number' ? plan.priceMonthly : plan.priceMonthly;
    } else {
      if (typeof plan.priceYearly === 'number') {
        const monthlyEquivalent = Math.round(plan.priceYearly / 12);
        return monthlyEquivalent;
      }
      return plan.priceYearly;
    }
  };

  const faqs = [
    {
      question: 'Can I change plans later?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, PayPal, and bank transfers for annual subscriptions.',
    },
    {
      question: 'Is there a setup fee?',
      answer: 'No setup fees. You only pay the subscription fee for your chosen plan.',
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes, you can cancel your subscription at any time. No long-term contracts required.',
    },
    {
      question: 'Do you offer discounts for annual billing?',
      answer: 'Yes! Save up to 20% when you choose annual billing instead of monthly.',
    },
    {
      question: 'What happens after the trial ends?',
      answer: 'After your 7-day trial, you can choose a plan to continue. Your data is preserved.',
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-200">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 gradient-bg dark:border-b dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white dark:text-white mb-4 md:mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg sm:text-xl text-primary-100 dark:text-gray-400 max-w-3xl mx-auto mb-8">
            Choose the perfect plan for your business. Start with a 7-day free trial, no credit card required.
          </p>

          {/* Billing Toggle - iOS Style */}
          <div className="flex items-center justify-center gap-4 bg-white/10 dark:bg-gray-800 dark:border dark:border-gray-700 backdrop-blur-sm rounded-2xl p-2 max-w-md mx-auto">
            <span className={`text-sm sm:text-base font-medium transition-colors ${billingCycle === 'monthly' ? 'text-white dark:text-white' : 'text-primary-200 dark:text-gray-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative inline-flex h-8 w-14 sm:h-10 sm:w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600"
              style={{ backgroundColor: billingCycle === 'yearly' ? '#10b981' : '#6b7280' }}
              aria-label="Toggle billing cycle"
            >
              <span
                className={`inline-block h-6 w-6 sm:h-8 sm:w-8 transform rounded-full bg-white shadow-lg transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-7 sm:translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
            <div className="flex flex-col items-start">
              <span className={`text-sm sm:text-base font-medium transition-colors ${billingCycle === 'yearly' ? 'text-white dark:text-white' : 'text-primary-200 dark:text-gray-400'}`}>
                Yearly
              </span>
              {billingCycle === 'yearly' && (
                <span className="text-xs text-green-300 dark:text-green-400 font-semibold">Save up to 20%</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 md:py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-4">Loading plans...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {plans.map((plan, index) => {
                  const savings = calculateSavings(plan.priceMonthly, plan.priceYearly);
                  return (
                    <div
                      key={index}
                      className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900/50 p-6 sm:p-8 flex flex-col border border-transparent dark:border-gray-700 ${
                        plan.popular ? 'ring-2 ring-primary-500 scale-105 lg:scale-110' : ''
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap">
                            Most Popular
                          </span>
                        </div>
                      )}

                      {billingCycle === 'yearly' && savings > 0 && (
                        <div className="absolute -top-4 right-4">
                          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            Save {savings}%
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {plan.name}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-4 min-h-[2.5rem] sm:min-h-[3rem]">
                          {plan.description}
                        </p>
                        <div className="flex flex-col items-center justify-center">
                          <div className="flex items-baseline">
                            <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white">
                              ${getDisplayPrice(plan)}
                            </span>
                            {typeof plan.priceMonthly === 'number' && (
                              <span className="text-gray-600 dark:text-gray-400 ml-2 text-sm">/mo</span>
                            )}
                          </div>
                          {/* Ethiopian Birr Equivalent */}
                          {billingCycle === 'monthly' && typeof plan.priceMonthly === 'number' && (
                            <div className="text-xs sm:text-sm text-gray-500 mt-1">
                              ≈ {(plan.priceMonthly * USD_TO_ETB).toLocaleString()} ETB/mo
                            </div>
                          )}
                          {billingCycle === 'yearly' && typeof plan.priceYearly === 'number' && (
                            <>
                              <div className="text-xs sm:text-sm text-gray-500 mt-1">
                                ≈ {(Math.round(plan.priceYearly / 12) * USD_TO_ETB).toLocaleString()} ETB/mo
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 mt-2">
                                ${plan.priceYearly} billed annually
                                <div className="text-xs text-gray-400 dark:text-gray-600">
                                  (≈ {(plan.priceYearly * USD_TO_ETB).toLocaleString()} ETB)
                                </div>
                              </div>
                            </>
                          )}
                          {billingCycle === 'monthly' && typeof plan.priceMonthly === 'number' && (
                            <div className="text-xs text-gray-400 mt-1">
                              Billed monthly
                            </div>
                          )}
                        </div>
                      </div>

                      <ul className="space-y-3 mb-6 flex-grow">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <CheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <Link
                        to="/request-trial"
                        className={`w-full block text-center py-3 px-4 sm:px-6 rounded-lg font-semibold transition-all text-sm sm:text-base ${
                          plan.popular
                            ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {plan.name === 'Enterprise' ? 'Contact Sales' : 'Start Free Trial'}
                      </Link>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 sm:mt-12 text-center">
                <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-4">
                  All plans include 7-day free trial • No credit card required • Cancel anytime
                </p>
                {billingCycle === 'monthly' && (
                  <button
                    onClick={() => setBillingCycle('yearly')}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                  >
                    💰 Switch to yearly and save up to 20%
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Comparison Table - Desktop Only */}
      <section className="py-12 md:py-16 hidden lg:block bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Compare Plans
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Find the perfect fit for your business needs
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900/50 overflow-hidden border dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Feature
                    </th>
                    {plans.map((plan, idx) => (
                      <th key={idx} className="px-6 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {!loading && plans.length > 0 && (
                    <>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">Users</td>
                        {plans.map((plan, idx) => (
                          <td key={idx} className="px-6 py-4 text-center text-sm text-gray-600 dark:text-gray-400">
                            {plan.limits?.max_users === -1 ? 'Unlimited' : plan.limits?.max_users || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-gray-50 dark:bg-gray-900">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">Products</td>
                        {plans.map((plan, idx) => (
                          <td key={idx} className="px-6 py-4 text-center text-sm text-gray-600 dark:text-gray-400">
                            {plan.limits?.max_products === -1 ? 'Unlimited' : plan.limits?.max_products?.toLocaleString() || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">Warehouses</td>
                        {plans.map((plan, idx) => (
                          <td key={idx} className="px-6 py-4 text-center text-sm text-gray-600 dark:text-gray-400">
                            {plan.limits?.max_warehouses === -1 ? 'Unlimited' : plan.limits?.max_warehouses || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-gray-50 dark:bg-gray-900">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">API Access</td>
                        {plans.map((plan, idx) => (
                          <td key={idx} className="px-6 py-4 text-center">
                            {plan.features?.includes('API Access') || plan.features?.includes('api_access') ? (
                              <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                            ) : (
                              <span className="text-gray-400 dark:text-gray-600">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">Dedicated Database</td>
                        {plans.map((plan, idx) => (
                          <td key={idx} className="px-6 py-4 text-center">
                            {plan.features?.includes('Dedicated Database') || plan.features?.includes('dedicated_database') ? (
                              <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                            ) : (
                              <span className="text-gray-400 dark:text-gray-600">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-4 sm:p-6 border dark:border-gray-700">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {faq.question}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Large CTA Banner - Still Have Questions */}
      <section className="py-16 md:py-20 gradient-bg">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 md:mb-6">
              Still Have Questions?
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-primary-100 mb-8 md:mb-10 max-w-3xl mx-auto">
              Our team is here to help you find the perfect plan for your business. Get in touch and we'll answer all your questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Link 
                to="/contact" 
                className="btn-primary bg-white text-primary-600 hover:bg-gray-100 text-lg px-8 py-4 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
              >
                Contact Our Team
              </Link>
              <Link 
                to="/request-trial" 
                className="btn-outline text-white border-white hover:bg-white/10 text-lg px-8 py-4"
              >
                Start Free Trial
              </Link>
            </div>
            
            {/* Contact Methods */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-2xl mx-auto">
              <a 
                href="mailto:support@stockwise-inventory.cloud"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-6 py-4 text-white transition-all hover:scale-105 w-full sm:w-auto"
              >
                <span className="text-2xl">📧</span>
                <div className="text-left">
                  <div className="text-sm text-primary-100">Email Us</div>
                  <div className="font-semibold">support@stockwise-inventory.cloud</div>
                </div>
              </a>
              
              <a 
                href="https://t.me/stockwiseinventory96"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-6 py-4 text-white transition-all hover:scale-105 w-full sm:w-auto"
              >
                <span className="text-2xl">💬</span>
                <div className="text-left">
                  <div className="text-sm text-primary-100">Chat on Telegram</div>
                  <div className="font-semibold">@stockwiseinventory96</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
